import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Upload,
  Button,
  message,
  Divider,
} from "antd";
import {
  UploadOutlined,
  InboxOutlined,
  DeleteOutlined,
  DeleteColumnOutlined,
  DeleteFilled,
} from "@ant-design/icons";
import { useGetCategoriesQuery } from "@/services/api/category/categoryApi";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import {
  useAddProductMutation,
  useGetAllProductQuery,
  useGetProductsQuery,
} from "@/services/api/product/productApi";
import { useGetUnitOfMeasurementQuery } from "@/services/api/unitOfMeasurement/unitOfMeasurementApi";

import "./AddProductModal.css";
import { DeleteOutline, Inbox } from "@mui/icons-material";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";
import { useGetSidesQuery } from "@/services/api/sides/getSidesApi";
import useAuth from "@/hooks/useAuth";

const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const AddProductModal = ({
  visible,
  onCancel,
  onSuccess,
  productType = "Product",
  categoryId = null,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [imageUrl, setImageUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [addDiscount, setAddDiscount] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [canBeAddOn, setCanBeAddOn] = useState(false);
  const [isVariant, setIsVariant] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const authUser = useAuth();
  const [addProduct, { isLoading: isAdding }] = useAddProductMutation();
  const { data: sides = [], isLoading: sidesLoading } = useGetSidesQuery();
  const { data: categories = [], isLoading: categoriesLoading } =
    useGetCategoriesQuery();
  const { data: locations = [], isLoading: locationsLoading } =
    useGetLocationsQuery();
  const { data: units = [], isLoading: unitsLoading } =
    useGetUnitOfMeasurementQuery();
  const { data: products = [], isLoading: productsLoading } =
    //Changed to useGetAllProductQuery to fetch all products for variant selection
    useGetAllProductQuery();
  // const { data: products = [], isLoading: productsLoading } = useGetProductsQuery();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setFileList([]);
      setImageUrl(null);
      setPreviewUrl(null);
      setAddDiscount(false);
      setIsActive(false);
      setCanBeAddOn(false);
      setIsVariant(false);
      setSelectedLocations([]);
      if (categoryId) {
        form.setFieldsValue({ category_id: categoryId });
      }
    }
  }, [visible, form, categoryId]);

  // Handle location selection with "All" option
  const handleLocationChange = (values) => {
    if (values.includes("all")) {
      // If "All" is selected, only keep "All"
      setSelectedLocations(["all"]);
      form.setFieldsValue({ location_id: ["all"] });
    } else {
      // Regular selection
      setSelectedLocations(values);
    }
  };

  // ✅ Upload image to Cloudinary
  const handleUpload = async (file) => {
    try {
      message.loading({ content: "Uploading image...", key: "upload" });
      const url = await uploadToCloudinary(file);
      // console.log("Cloudinary URL:", url);
      setImageUrl(url);
      form.setFieldsValue({ image: url }); // Set form field value for validation
      message.success({
        content: "Image uploaded successfully!",
        key: "upload",
      });
      return url;
    } catch (error) {
      console.error("Upload error:", error);
      message.error({ content: "Failed to upload image", key: "upload" });
      throw error;
    }
  };

  // ✅ Handle upload change and show preview immediately
  const handleUploadChange = ({ file, fileList: newFileList }) => {
    const latestFile = newFileList.slice(-1)[0];
    setFileList(newFileList.slice(-1)); // Only one image

    if (latestFile && latestFile.originFileObj) {
      // Instant preview
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(latestFile.originFileObj);
    }

    if (file.status === "removed") {
      setFileList([]);
      setPreviewUrl(null);
      setImageUrl(null);
      form.setFieldsValue({ image: null }); // Clear form field value
    }
  };

  const handleRemoveImage = () => {
    setFileList([]);
    setPreviewUrl(null);
    setImageUrl(null);
    form.setFieldsValue({ image: null }); // Clear form field value
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    fileList,
    customRequest: async ({ file, onSuccess, onError }) => {
      // console.log("customRequest called with file:", file);
      try {
        const url = await handleUpload(file);
        // console.log("Upload successful, URL:", url);
        onSuccess("ok");
      } catch (error) {
        console.error("customRequest error:", error);
        onError(error);
      }
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return Upload.LIST_IGNORE;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error("Image must be smaller than 2MB!");
        return Upload.LIST_IGNORE;
      }
      return true; // Allow upload to proceed to customRequest
    },
    onChange: handleUploadChange,
    onRemove: handleRemoveImage,
  };

  // ✅ Format & parse price fields
  const formatPrice = (value) =>
    value
      ? value.replace(/[^\d]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : "";
  const parsePrice = (value) => (value ? value.replace(/,/g, "") : "");

  // Handle validation failure
  const handleValidationFailed = (errorInfo) => {
    // console.log("Validation failed:", errorInfo);
    message.error("Please fill all required fields");
  };

  // ✅ Submit form
  const handleSubmit = async (values) => {
    // console.log("Image URL on submit:", imageUrl);
    // console.log("Form values:", values);

    try {
      // Handle "All" locations selection
      let locationIds = values.location_id;
      if (locationIds.includes("all")) {
        // If "All" is selected, pass all available location IDs
        locationIds = locations.map((loc) => loc.id);
      }

      const payload = {
        category_id: values.category_id,
        location_id: locationIds,
        name: values.name,
        description: values.description,
        ingredients: values.ingredient,
        minimum_qty: parseInt(values.minimum_qty),
        current_qty: parseInt(values.current_qty),
        serving_size: parseInt(values.serving_size), // Always required now
        amount: parseInt(parsePrice(values.amount)),
        status: isActive ? 1 : 0,
        unit_of_measurement_id: values.unit_of_measurement_id,
        created_by: authUser?.id,
        can_be_addon: canBeAddOn ? 1 : 0,
      };

      if (addDiscount && values.discount) {
        payload.discount = parseInt(parsePrice(values.discount));
      }

      if (values.add_ons?.length > 0) {
        payload.product_addon = values.add_ons;
      }

      // Include variant information - always send is_variant
      payload.is_variant = isVariant ? 1 : 0;

      // Only include variant_product_id if variant is enabled and a product is selected
      if (isVariant && values.variant_product_id) {
        payload.variant_product_id = values.variant_product_id;
      }

      // Include image only if uploaded (optional)
      if (imageUrl) {
        payload.product_image = imageUrl;
      }

      await addProduct(payload).unwrap();
      message.success(`${productType} added successfully!`);
      onSuccess?.();
      onCancel();
    } catch (error) {
      console.error("Error adding product:", error);
      message.error("An error occured");
    }
  };

  const getModalTitle = () => {
    switch (productType) {
      case "Platter":
        return "Add Platter";
      case "Non-edibles":
        return "Add Non-edibles";
      default:
        return "Add Product";
    }
  };

  return (
    <Modal
      title={getModalTitle()}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={900}
      destroyOnClose
      className="add-product-modal"
    >
      <div className="modal-content-wrapper">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleValidationFailed}
          className="modal-form"
        >
          <div className="modal-body">
            {/* BASIC INFORMATION SECTION */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-700 mb-4">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <Form.Item
                  name="name"
                  label="Product Name"
                  rules={[
                    { required: true, message: "Please enter product name" },
                  ]}
                  className="mb-0"
                >
                  <Input
                    placeholder="e.g. Party Jollof"
                    size="large"
                    className="rounded-md"
                  />
                </Form.Item>

                <Form.Item
                  name="category_id"
                  label="Category"
                  rules={[
                    { required: true, message: "Please select a category" },
                  ]}
                  className="mb-0"
                >
                  <Select
                    placeholder="Select Category"
                    loading={categoriesLoading}
                    disabled={!!categoryId}
                    size="large"
                    className="rounded-md"
                  >
                    {categories.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    {
                      required: true,
                      message: "Please enter product description",
                    },
                  ]}
                  className="mb-0 md:col-span-2"
                >
                  <TextArea
                    placeholder="Briefly describe the product"
                    rows={3}
                    className="rounded-md"
                  />
                </Form.Item>

                <Form.Item
                  name="ingredient"
                  label="Ingredients"
                  rules={[
                    { required: true, message: "Please enter ingredients" },
                  ]}
                  className="mb-0 md:col-span-2"
                >
                  <Input
                    placeholder="Input the active ingredients"
                    size="large"
                    className="rounded-md"
                  />
                </Form.Item>
              </div>
            </div>

            <Divider className="my-6" />

            {/* PRODUCT VARIANT SECTION */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-700 mb-4">
                Product Variant
              </h3>
              <div className="bg-purple-50 p-4 rounded-lg">
                <Form.Item label="" className="mb-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-gray-700 font-medium block">
                        This is a variant of another product
                      </span>
                      <span className="text-sm text-gray-500">
                        Use this when creating different versions of the same
                        dish (e.g., Egusi with Uziza and Egusi with Bitterleaf
                        are variants of Egusi Soup)
                      </span>
                    </div>
                    <Switch
                      checked={isVariant}
                      onChange={setIsVariant}
                      className="bg-gray-300"
                    />
                  </div>
                </Form.Item>

                {isVariant && (
                  <Form.Item
                    name="variant_product_id"
                    label="Parent Product"
                    rules={[
                      {
                        required: isVariant,
                        message: "Please select the parent product",
                      },
                    ]}
                    className="mb-0 mt-4"
                  >
                    <Select
                      placeholder="Select Parent Product"
                      showSearch
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      size="large"
                      className="rounded-md"
                      loading={productsLoading}
                    >
                      {products?.map((item) => (
                        <Option key={item.id} value={item.id}>
                          {item.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </div>
            </div>

            <Divider className="my-6" />

            {/* PRICING & INVENTORY SECTION */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-700 mb-4">
                Pricing & Inventory
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <Form.Item
                  name="amount"
                  label="Price (per unit)"
                  rules={[{ required: true, message: "Please enter price" }]}
                  className="mb-0"
                >
                  <Input
                    prefix="₦"
                    placeholder="e.g. 500"
                    size="large"
                    className="rounded-md"
                    onChange={(e) => {
                      const formatted = formatPrice(e.target.value);
                      e.target.value = formatted;
                      form.setFieldsValue({ amount: formatted });
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="unit_of_measurement_id"
                  label="Measurement Unit"
                  rules={[
                    {
                      required: true,
                      message: "Please select measurement unit",
                    },
                  ]}
                  className="mb-0"
                >
                  <Select
                    placeholder="Select Unit"
                    loading={unitsLoading}
                    size="large"
                    className="rounded-md"
                  >
                    {units.map((unit) => (
                      <Option key={unit.id} value={unit.id}>
                        {unit.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="current_qty"
                  label="Current Quantity"
                  rules={[
                    {
                      required: true,
                      message: "Please enter current quantity",
                    },
                  ]}
                  className="mb-0"
                >
                  <Input
                    type="number"
                    min="0"
                    size="large"
                    placeholder="0"
                    className="rounded-md"
                  />
                </Form.Item>

                <Form.Item
                  name="minimum_qty"
                  label="Minimum Stock Level"
                  rules={[
                    { required: true, message: "Please enter minimum stock" },
                  ]}
                  className="mb-0"
                >
                  <Input
                    type="number"
                    min="0"
                    size="large"
                    placeholder="0"
                    className="rounded-md"
                  />
                </Form.Item>

                <Form.Item
                  name="serving_size"
                  label="Serving Size"
                  rules={[
                    { required: true, message: "Please enter serving size" },
                  ]}
                  className="mb-0"
                >
                  <Input
                    type="number"
                    min="0"
                    size="large"
                    placeholder="e.g. 2"
                    className="rounded-md"
                  />
                </Form.Item>

                <Form.Item
                  name="location_id"
                  label="Available Locations"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one location",
                    },
                  ]}
                  className="mb-0"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select Locations"
                    loading={locationsLoading}
                    size="large"
                    className="rounded-md"
                    value={selectedLocations}
                    onChange={handleLocationChange}
                  >
                    <Option key="all" value="all">
                      All Locations
                    </Option>
                    {locations.map((loc) => (
                      <Option key={loc.id} value={loc.id}>
                        {loc.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
            </div>

            <Divider className="my-6" />

            {/* DISCOUNT SECTION */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-700 mb-4">
                Discount (Optional)
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <Form.Item label="" className="mb-0">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">
                      Enable discount for this product
                    </span>
                    <Switch
                      checked={addDiscount}
                      onChange={setAddDiscount}
                      className="bg-gray-300"
                    />
                  </div>
                </Form.Item>

                {addDiscount && (
                  <Form.Item
                    name="discount"
                    label="Discount Amount"
                    rules={[
                      {
                        required: addDiscount,
                        message: "Please enter discount amount",
                      },
                    ]}
                    className="mb-0 mt-4"
                  >
                    <Input
                      prefix="₦"
                      placeholder="e.g. 100"
                      size="large"
                      className="rounded-md"
                      onChange={(e) => {
                        const formatted = formatPrice(e.target.value);
                        e.target.value = formatted;
                        form.setFieldsValue({ discount: formatted });
                      }}
                    />
                  </Form.Item>
                )}
              </div>
            </div>

            <Divider className="my-6" />

            {/* ADD-ONS & OPTIONS SECTION */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-700 mb-4">
                Add-Ons & Options
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <Form.Item
                  name="add_ons"
                  label="Available Add-Ons (Optional)"
                  className="mb-0"
                  tooltip="Select add-ons that customers can choose with this product"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select Add-Ons"
                    loading={sidesLoading}
                    size="large"
                    className="rounded-md"
                    allowClear
                  >
                    {sides.map((side) => (
                      <Option key={side.id} value={side.id}>
                        {side.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <Form.Item label="" className="mb-0">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="text-gray-700 font-medium block">
                          This product can be used as an add-on
                        </span>
                        <span className="text-sm text-gray-500">
                          Allow customers to add this item to other products
                        </span>
                      </div>
                      <Switch
                        checked={canBeAddOn}
                        onChange={setCanBeAddOn}
                        className="bg-gray-300"
                      />
                    </div>
                  </Form.Item>
                </div>
              </div>
            </div>

            <Divider className="my-6" />

            {/* IMAGE UPLOAD SECTION */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-700 mb-4">
                Product Image (Optional)
              </h3>
              <Form.Item name="image" className="mb-0">
                <Dragger
                  {...uploadProps}
                  className="border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-400 transition-colors"
                >
                  <p className="ant-upload-drag-icon">
                    <Inbox className="text-5xl text-gray-400" />
                  </p>
                  <p className="ant-upload-text text-gray-700 font-medium">
                    Click or drag file to upload
                  </p>
                  <p className="ant-upload-hint text-sm text-gray-500">
                    Supports: JPG, PNG, GIF (Max 2MB)
                  </p>
                </Dragger>

                {(previewUrl || imageUrl) && (
                  <div className="mt-4 flex justify-center">
                    <div className="relative inline-block">
                      <img
                        src={previewUrl || imageUrl}
                        alt="preview"
                        className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                      />
                      <Button
                        type="text"
                        icon={<DeleteOutline />}
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full shadow-md hover:bg-green-600 border border-green-600"
                        size="small"
                      />
                    </div>
                  </div>
                )}
              </Form.Item>
            </div>

            <Divider className="my-6" />

            {/* VISIBILITY SECTION */}
            <div className="mb-2">
              <h3 className="text-base font-semibold text-gray-700 mb-4">
                Product Visibility
              </h3>
              <div className="bg-green-50 p-4 rounded-lg">
                <Form.Item label="" className="mb-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-gray-700 font-medium block">
                        Active (Visible to Customers)
                      </span>
                      <span className="text-sm text-gray-500">
                        Make this product available for ordering
                      </span>
                    </div>
                    <Switch
                      checked={isActive}
                      onChange={setIsActive}
                      className="bg-gray-300"
                    />
                  </div>
                </Form.Item>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer flex justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <Button size="large" onClick={onCancel} className="px-6">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isAdding}
              size="large"
              className="bg-orange-500 border-orange-500 hover:bg-orange-600 px-6"
            >
              {isAdding ? "Adding..." : `Add ${productType}`}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default AddProductModal;
